import React, { useState } from 'react';
import {
  LayoutDashboard,
  BookOpen,
  RotateCcw,
  FileCheck2,
  FileText,
  Layers,
  Users,
  CalendarCheck,
  Target,
  Newspaper,
  HelpCircle,
  Bell,
  ShieldCheck,
  Crown,
  Menu,
  X,
  Search,
  ChevronDown,
  BarChart3
} from 'lucide-react';
import { UserProfile } from '../types';
import { isUserAdmin, isUserSubscribed } from '../lib/firebase';
import { PrePraxisBadge } from './brand/PrePraxisBadge';

// =====================================================================
// Logged-in app shell (2026-10-07 navy redesign): a navy left sidebar with
// every section, plus a slim white top bar (search + account). Replaces
// the old logged-in top tab bar in Navbar.tsx. The logged-out marketing
// navbar in Navbar.tsx is unchanged. On small screens the sidebar becomes
// a slide-in drawer opened from the top bar's menu button.
// =====================================================================

export interface AppShellNavProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  userProfile: UserProfile;
  dueReviewCount?: number;
  openPricingModal: () => void;
  onOpenFlashcards: () => void;
  onOpenGoalExam: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
  onClick?: () => void;
}

const SidebarContent: React.FC<AppShellNavProps & { onNavigate?: () => void }> = ({
  activeTab,
  setActiveTab,
  userProfile,
  dueReviewCount = 0,
  openPricingModal,
  onOpenFlashcards,
  onOpenGoalExam,
  onNavigate
}) => {
  const isAdmin = isUserAdmin(userProfile);
  const subscribed = isUserSubscribed(userProfile);

  const main: NavItem[] = [
    { id: 'dashboard', label: isAdmin ? 'Admin Dashboard' : 'Dashboard', icon: isAdmin ? ShieldCheck : LayoutDashboard },
    ...(isAdmin ? [{ id: 'analytics', label: 'Analytics', icon: BarChart3 } as NavItem] : []),
    { id: 'papers', label: 'Exams Directory', icon: BookOpen },
    { id: 'review', label: 'Review Queue', icon: RotateCcw, badge: dueReviewCount > 0 ? dueReviewCount : undefined },
    { id: 'mock-exams', label: 'Mock Exams', icon: FileCheck2 },
    { id: 'guides', label: 'Study Guides', icon: FileText },
    { id: 'resources', label: 'Flashcards', icon: Layers, onClick: onOpenFlashcards },
    { id: 'forum', label: 'Community', icon: Users },
    { id: 'tutoring', label: '1-on-1 Tutoring', icon: CalendarCheck }
  ];
  const quick: NavItem[] = [
    { id: 'goal', label: 'My Goal Exam', icon: Target, onClick: onOpenGoalExam },
    { id: 'reminders', label: 'Study Reminders', icon: Bell },
    { id: 'blog', label: 'Blog', icon: Newspaper },
    { id: 'faq', label: 'FAQ & Support', icon: HelpCircle }
  ];

  const go = (item: NavItem) => {
    if (item.onClick) item.onClick();
    else setActiveTab(item.id);
    onNavigate?.();
  };

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = activeTab === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => go(item)}
        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] transition cursor-pointer ${
          active
            ? 'bg-[#0E9594] text-white font-bold shadow-[0_6px_16px_rgba(14,149,148,0.35)]'
            : 'text-indigo-100/90 hover:bg-white/10 hover:text-white'
        }`}
      >
        <Icon className="w-[18px] h-[18px] shrink-0" />
        <span className="truncate">{item.label}</span>
        {item.badge !== undefined && (
          <span className="ml-auto text-[11px] font-extrabold bg-pink-500 text-white rounded-full px-2 py-0.5">{item.badge}</span>
        )}
      </button>
    );
  };

  return (
    <div className="h-full flex flex-col px-4 py-5 overflow-y-auto">
      <button type="button" onClick={() => { setActiveTab('dashboard'); onNavigate?.(); }} className="flex items-center gap-2.5 px-1.5 pb-5 text-left cursor-pointer">
        <span className="w-10 h-10 rounded-xl bg-[#0E9594] flex items-center justify-center shrink-0">
          <PrePraxisBadge className="w-8 h-8 text-white" showLaurels={true} showCap={true} />
        </span>
        <span>
          <span className="block text-white text-lg font-extrabold leading-tight">Pre-Praxis PREP</span>
          <span className="block text-[10.5px] text-indigo-200/80">{isAdmin ? 'Administrator view' : 'Teacher Certification Prep'}</span>
        </span>
      </button>

      <nav className="space-y-1">{main.map(renderItem)}</nav>

      <div className="text-[10.5px] font-bold tracking-[0.14em] text-indigo-300/70 mt-5 mb-2 px-3.5">QUICK LINKS</div>
      <nav className="space-y-1">{quick.map(renderItem)}</nav>

      {!isAdmin && !subscribed && (
        <div className="mt-auto pt-5">
          <div className="rounded-2xl p-4 border border-[#2c4296] bg-gradient-to-br from-[#1b2f7a] to-[#162a6b]">
            <div className="flex items-center gap-2 text-white font-extrabold">
              <Crown className="w-4 h-4 text-amber-300" /> Go All-Access
            </div>
            <p className="text-[12px] text-indigo-100/80 leading-relaxed mt-1.5 mb-3">
              Every paper, every mock exam, all study guides and flashcards.
            </p>
            <button
              type="button"
              onClick={() => { openPricingModal(); onNavigate?.(); }}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white font-extrabold text-[13px] rounded-xl py-2.5 transition cursor-pointer"
            >
              Upgrade, $35/mo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const AppSidebar: React.FC<AppShellNavProps> = (props) => (
  <aside className="hidden lg:block fixed inset-y-0 left-0 w-[260px] z-30 bg-gradient-to-b from-[#0f1b4d] to-[#131f5c]">
    <SidebarContent {...props} />
  </aside>
);

interface AppTopBarProps extends AppShellNavProps {
  onOpenAccount: () => void;
  onSearchExams: (query: string) => void;
}

export const AppTopBar: React.FC<AppTopBarProps> = (props) => {
  const { userProfile, onOpenAccount, onSearchExams } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const initial = (userProfile.displayName || userProfile.email || 'P').trim().charAt(0).toUpperCase();

  return (
    <>
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="h-16 flex items-center gap-3 px-4 sm:px-6">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden p-2 rounded-xl text-slate-600 hover:bg-slate-100 cursor-pointer"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="lg:hidden font-extrabold text-[#0f1b4d]">Pre-Praxis PREP</span>

          <form
            onSubmit={(e) => { e.preventDefault(); onSearchExams(query.trim()); }}
            className="hidden md:flex items-center flex-1 max-w-xl bg-slate-50 border border-slate-200 rounded-xl px-3.5 h-10 focus-within:ring-2 focus-within:ring-[#0E9594]/40"
          >
            <Search className="w-4 h-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search exams by state, name or code (e.g. 5733)"
              className="flex-1 bg-transparent pl-2.5 text-[13.5px] focus:outline-none"
            />
          </form>

          <span className="hidden xl:block text-xs italic text-[#0E9594] ml-2">Your goals · Our support · A brighter future</span>

          <button
            type="button"
            onClick={onOpenAccount}
            className="ml-auto flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
          >
            <span className="w-9 h-9 rounded-full bg-[#0f1b4d] text-white font-extrabold flex items-center justify-center">{initial}</span>
            <span className="hidden sm:block text-sm font-bold text-slate-800 max-w-[140px] truncate">{userProfile.displayName || 'My Account'}</span>
            <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
          </button>
        </div>
      </header>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-950/50" onClick={() => setDrawerOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[280px] bg-gradient-to-b from-[#0f1b4d] to-[#131f5c] shadow-2xl">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-3 top-3 p-2 rounded-lg text-indigo-100 hover:bg-white/10 cursor-pointer"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent {...props} onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};
